import React, { useState } from "react";
//CSS
import styles from "./InviteExistingUser.module.css";
import Form from "../../Form/Form";
import FloatingLabelSelect from "../../Form/FloatingLabelSelect/FloatingLabelSelect";
import { FloatingLabelSelectOption } from "../../../../Types/Form/FloatingLabelSelectOption";
import Button from "../../Button/Button";
import { InviteNewUserRequest } from "../../../../Types/InviteRemoveUser/InviteNewUserRequest";
import { InviteUserDisplayInfo } from "../../../../Types/InviteRemoveUser/InviteUserDisplayInfo";
import { axiosInstance } from "../../../../Authentication/AxiosInterceptor";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

type InviteExistingUserProps = {
  users: FloatingLabelSelectOption[];
  roles: FloatingLabelSelectOption[];
  addClientUser: (user: InviteUserDisplayInfo) => void;
  areaId?: string;
  userInfoMap?: Map<string, InviteUserDisplayInfo>;
  disabled?: boolean;
  onInvite?: (user: InviteUserDisplayInfo, role: string) => Promise<void>;
  /** Explains why the user list is limited, for example a portfolio linked to a partner. */
  scopeNote?: string;
};

const InviteExistingUser = ({
  users,
  roles,
  addClientUser,
  areaId: propAreaId,
  userInfoMap,
  disabled = false,
  onInvite,
  scopeNote,
}: InviteExistingUserProps) => {
  const { areaId: paramAreaId } = useParams();
  const areaId = propAreaId || paramAreaId;

  const [selectedUserSub, setSelectedUserSub] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("");

  const handleUserIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUserSub(e.target.value);
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(e.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedUserSub || !selectedRole) {
      toast.error("Please select a user and role");
      return;
    }

    let userEmail = "";
    let userName = "";
    
    if (userInfoMap?.has(selectedUserSub)) {
      const userInfo = userInfoMap.get(selectedUserSub)!;
      userEmail = userInfo.email;
      userName = userInfo.fullName;
    } else {
      const selectedUser = users.find(u => u.value === selectedUserSub);
      const match = selectedUser?.label.match(/^(.+?)\s*\((.+?)\)$/);
      if (match) {
        userName = match[1].trim();
        userEmail = match[2].trim();
      } else {
        toast.error("Could not find user information");
        return;
      }
    }

    const person: InviteUserDisplayInfo = {
      sub: selectedUserSub,
      fullName: userName,
      email: userEmail,
    };
    if (onInvite) {
      try {
        await onInvite(person, selectedRole);
        toast.success("User invited successfully!");
        setSelectedUserSub("");
        setSelectedRole("");
        addClientUser(person);
      } catch (error) {
        console.error("Error inviting user:", error);
        toast.error("Failed to invite user. Please try again.");
      }
      return;
    }

    if (!areaId) {
      toast.error("Please select a user and role");
      return;
    }

    const nameParts = userName.split(" ");
    const givenName = nameParts[0] || "";
    const familyName = nameParts.slice(1).join(" ") || "";

    const inviteData: InviteNewUserRequest = {
      user: {
        given_name: givenName,
        family_name: familyName,
        email: userEmail,
        name: userName,
      },
      roles: selectedRole,
      type: "Evaluate",
      areaId: areaId,
      clientId: process.env.REACT_APP_CLIENT_ID || "",
    };

    try {
      const response = await axiosInstance.post("InviteNewProductUser", inviteData);
      if (response.status === 200) {
        toast.success("User invited successfully!");
        setSelectedUserSub("");
        setSelectedRole("");
        addClientUser(response.data as InviteUserDisplayInfo);
      }
    } catch (error) {
      console.error("Error inviting user:", error);
      toast.error("Failed to invite user. Please try again.");
    }
  };

  return (
    <div className={styles.container}>
      <h3>Invite Existing User</h3>
      {scopeNote && <p className={styles.scopeNote}>{scopeNote}</p>}
      <Form>
        <FloatingLabelSelect
          label="Select User"
          options={users}
          value={selectedUserSub}
          onChange={handleUserIdChange}
          disabled={disabled}
        />
        <FloatingLabelSelect
          label="Select Role"
          options={roles}
          value={selectedRole}
          onChange={handleRoleChange}
          disabled={disabled}
        />
        <Button type="button" size={"Small"} clickHandler={handleSubmit} disabled={disabled}>
          Invite User
        </Button>
      </Form>
    </div>
  );
};

export default InviteExistingUser;
